import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Tabs, Tab, TextField, Button, Typography, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Divider, CircularProgress
} from '@mui/material';
import {
  Plus, Save, Trash2, Pencil,
  Eye, Flag, Target, Bell, Droplets, Building2, User, Sprout, Heart, Bike, Star, Mountain, Tent,
  Footprints, Sailboat, Cloud, Activity, Waves, MountainSnow, Minus, Upload, Image as ImageIcon
} from 'lucide-react';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader } from '../components/ui';
import './AboutManagement.css';
import './ModuleAdmin.css';

// Professional Lucide icons – keys must match frontend About.jsx ICON_COMPONENTS
const ICON_COMPONENTS = {
  eye: Eye,
  flag: Flag,
  target: Target,
  bell: Bell,
  drop: Droplets,
  building: Building2,
  person: User,
  shovel: Sprout,
  blood: Heart,
  cycle: Bike,
  star: Star,
  mountain: Mountain,
  tent: Tent,
  hike: Footprints,
  raft: Sailboat,
  parachute: Cloud,
  climb: Activity,
  dive: Waves,
  cave: Mountain,
  ski: MountainSnow
};

const ICON_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'eye', label: 'Vision / Eye' },
  { value: 'flag', label: 'Mission / Flag' },
  { value: 'target', label: 'Objective / Target' },
  { value: 'bell', label: 'Bell' },
  { value: 'drop', label: 'Water drop' },
  { value: 'building', label: 'Building / Heritage' },
  { value: 'person', label: 'Person' },
  { value: 'shovel', label: 'Plantation' },
  { value: 'blood', label: 'Blood donation' },
  { value: 'cycle', label: 'Cycling' },
  { value: 'star', label: 'Star / Leadership' },
  { value: 'mountain', label: 'Mountain' },
  { value: 'tent', label: 'Camping / Tent' },
  { value: 'hike', label: 'Trekking' },
  { value: 'raft', label: 'Rafting' },
  { value: 'parachute', label: 'Paragliding' },
  { value: 'climb', label: 'Rock climbing' },
  { value: 'dive', label: 'Scuba diving' },
  { value: 'cave', label: 'Caving' },
  { value: 'ski', label: 'Skiing' }
];

function getIconComponent(key) {
  if (!key) return null;
  return ICON_COMPONENTS[key] || null;
}

function splitGalleryUrls(raw) {
  if (!raw) return [];
  return String(raw)
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function removeGalleryUrlAt(raw, removeIndex) {
  const list = splitGalleryUrls(raw);
  return list.filter((_, idx) => idx !== removeIndex).join('\n');
}

function IconSelect({ value, onChange, label = 'Icon', size = 'small', fullWidth = true }) {
  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <InputLabel id={`icon-select-${label.replace(/\s/g, '-')}`}>{label}</InputLabel>
      <Select
        labelId={`icon-select-${label.replace(/\s/g, '-')}`}
        value={value || ''}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) => {
          const opt = ICON_OPTIONS.find((o) => o.value === v) || ICON_OPTIONS[0];
          const Icon = getIconComponent(opt.value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {Icon ? <Icon size={20} strokeWidth={2} className="about-icon-preview" /> : <Minus size={20} className="about-icon-preview" />}
              <span>{opt.label}</span>
            </Box>
          );
        }}
      >
        {ICON_OPTIONS.map((opt) => {
          const Icon = getIconComponent(opt.value);
          return (
            <MenuItem key={opt.value || 'none'} value={opt.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {Icon ? <Icon size={20} strokeWidth={2} className="about-icon-preview" /> : <Minus size={20} className="about-icon-preview" />}
                <span>{opt.label}</span>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const AboutManagement = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [tab, setTab] = useState(0);

  const { data: aboutData, isLoading } = useQuery('about-admin', adminAPI.getAbout);
  const about = aboutData?.data?.data || {};

  const hero = about.hero || {};
  const vision = about.vision || {};
  const mission = about.mission || {};
  const objective = about.objective || {};
  const sectionTitles = about.sectionTitles || {};
  const cta = about.cta || {};
  const projects = about.projects || [];
  const activities = about.activities || [];
  const journey = about.journey || [];

  const [heroForm, setHeroForm] = useState({ description: '', imageUrl: '', title: '', subtitle: '' });
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const heroImageInputRef = useRef(null);
  const [vmoForm, setVmoForm] = useState({ vision: {}, mission: {}, objective: {} });
  const [ctaForm, setCtaForm] = useState({ teamText: '', teamLink: '', volunteerText: '', volunteerLink: '' });
  const [sectionForm, setSectionForm] = useState({
    projects: '',
    projectsSubtitle: '',
    activities: '',
    journey: ''
  });

  useEffect(() => {
    if (!about.hero && !about.cta && !about.sectionTitles && !about.vision) return;
    if (about.hero) {
      setHeroForm({
        title: String(hero.title || hero.headline || '').trim(),
        subtitle: hero.subtitle || '',
        description: hero.description || '',
        imageUrl: hero.imageUrl || ''
      });
    }
    if (about.cta) setCtaForm({ teamText: cta.teamText || '', teamLink: cta.teamLink || '', volunteerText: cta.volunteerText || '', volunteerLink: cta.volunteerLink || '' });
    if (about.sectionTitles) {
      setSectionForm({
        projects: sectionTitles.projects || '',
        projectsSubtitle: sectionTitles.projectsSubtitle || '',
        activities: sectionTitles.activities || '',
        journey: sectionTitles.journey || ''
      });
    }
    if (about.vision || about.mission || about.objective) setVmoForm({ vision: about.vision || {}, mission: about.mission || {}, objective: about.objective || {} });
  }, [about.hero, about.cta, about.sectionTitles, about.vision, about.mission, about.objective]);

  const updateHeroMutation = useMutation(
    (data) => adminAPI.updateAboutHero(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'About hero updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update hero.', severity: 'error' })
    }
  );
  const updateVmoMutation = useMutation(
    (data) => adminAPI.updateAboutVmo(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Vision/Mission/Objective updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update VMO.', severity: 'error' })
    }
  );
  const updateCtaMutation = useMutation(
    (data) => adminAPI.updateAboutCta(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'About CTAs updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update CTAs.', severity: 'error' })
    }
  );
  const updateSectionTitlesMutation = useMutation(
    (data) => adminAPI.updateAboutSectionTitles(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'About section titles updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update section titles.', severity: 'error' })
    }
  );

  const createProjectMutation = useMutation(
    (data) => adminAPI.createAboutProject(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Project created.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to create project.', severity: 'error' })
    }
  );
  const updateProjectMutation = useMutation(
    ({ id, data }) => adminAPI.updateAboutProject(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Project updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update project.', severity: 'error' })
    }
  );
  const deleteProjectMutation = useMutation(
    (id) => adminAPI.deleteAboutProject(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Project deleted.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to delete project.', severity: 'error' })
    }
  );
  const createActivityMutation = useMutation(
    (data) => adminAPI.createAboutActivity(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Activity created.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to create activity.', severity: 'error' })
    }
  );
  const updateActivityMutation = useMutation(
    ({ id, data }) => adminAPI.updateAboutActivity(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Activity updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update activity.', severity: 'error' })
    }
  );
  const deleteActivityMutation = useMutation(
    (id) => adminAPI.deleteAboutActivity(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Activity deleted.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to delete activity.', severity: 'error' })
    }
  );
  const createJourneyMutation = useMutation(
    (data) => adminAPI.createAboutJourney(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Journey entry created.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to create journey entry.', severity: 'error' })
    }
  );
  const updateJourneyMutation = useMutation(
    ({ id, data }) => adminAPI.updateAboutJourney(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Journey entry updated.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to update journey entry.', severity: 'error' })
    }
  );
  const deleteJourneyMutation = useMutation(
    (id) => adminAPI.deleteAboutJourney(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-admin');
        showToast({ message: 'Journey entry deleted.', severity: 'success' });
      },
      onError: (e) =>
        showToast({ message: e.response?.data?.message || 'Failed to delete journey entry.', severity: 'error' })
    }
  );

  if (isLoading) return (
    <div className="about-admin-page module-page">
      <PageHeader title="About Us Management" subtitle="Loading about page content…" />
      <Card>
        <CardContent>
          <div className="ops-loading-box">
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="about-admin-page module-page">
      <PageHeader
        title="About Us Management"
        subtitle="Orange banner title, intro card, vision/mission/objective, projects grid, activities, journey and CTAs — same structure as the public About page."
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Banner & VMO" />
          <Tab label="Projects" />
          <Tab label="Activities" />
          <Tab label="Journey" />
          <Tab label="Section titles & CTAs" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <Card className="about-admin-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>About page — top sections</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Orange banner:</strong> main title + subtitle (white text), full width — same pattern as Special Events.
              <br />
              <strong>Intro card below:</strong> paragraph and optional background photo with dark overlay (not the orange strip).
            </Typography>

            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Orange banner</Typography>
            <div className="about-form-grid" style={{ marginBottom: 16 }}>
              <TextField
                fullWidth
                label="Banner title *"
                value={heroForm.title}
                onChange={(e) => setHeroForm((p) => ({ ...p, title: e.target.value }))}
                size="small"
                placeholder="e.g. About Invincible"
                sx={{ gridColumn: '1 / -1' }}
              />
              <TextField
                fullWidth
                label="Banner subtitle"
                value={heroForm.subtitle}
                onChange={(e) => setHeroForm((p) => ({ ...p, subtitle: e.target.value }))}
                size="small"
                placeholder="e.g. Adventure Awaits, Community Thrives"
                sx={{ gridColumn: '1 / -1' }}
              />
            </div>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Intro card (dark block under banner)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Intro paragraph *"
              value={heroForm.description}
              onChange={(e) => setHeroForm((p) => ({ ...p, description: e.target.value }))}
              size="small"
              sx={{ mb: 2 }}
              helperText="Shown as white text on the card; if empty, this card is hidden on the site."
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Background image for that intro card (optional). Upload or paste URL — appears behind the paragraph with a dark tint.
            </Typography>
            <div className="about-hero-image-section">
              <input
                ref={heroImageInputRef}
                type="file"
                accept="image/*"
                className="about-hero-image-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !file.type.startsWith('image/')) return;
                  setHeroImageUploading(true);
                  try {
                    const res = await adminAPI.uploadImage(file);
                    const url = res.data?.data?.url;
                    if (url) {
                      setHeroForm((p) => {
                        const next = { ...p, imageUrl: url };
                        updateHeroMutation.mutate({
                          ...next,
                          headline: next.title
                        });
                        return next;
                      });
                    }
                  } catch (err) {
                    showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
                  } finally {
                    setHeroImageUploading(false);
                    e.target.value = '';
                  }
                }}
              />
              {heroForm.imageUrl ? (
                <div className="about-hero-preview-wrap">
                  <div className="about-hero-preview" style={{ backgroundImage: `url(${heroForm.imageUrl})` }} />
                  <Button size="small" variant="outlined" startIcon={<Upload size={16} />} onClick={() => heroImageInputRef.current?.click()} disabled={heroImageUploading} sx={{ mt: 1 }}>
                    {heroImageUploading ? 'Uploading…' : 'Replace image (upload)'}
                  </Button>
                </div>
              ) : (
                <div className="about-hero-upload-zone" onClick={() => heroImageInputRef.current?.click()}>
                  <ImageIcon size={40} className="about-hero-upload-icon" />
                  <p className="about-hero-upload-text">{heroImageUploading ? 'Uploading…' : 'Upload intro background image'}</p>
                  <p className="about-hero-upload-hint">PNG, JPG or WebP recommended</p>
                </div>
              )}
            </div>
            <TextField
              fullWidth
              label="Or paste image URL"
              value={heroForm.imageUrl}
              onChange={(e) => setHeroForm((p) => ({ ...p, imageUrl: e.target.value }))}
              size="small"
              placeholder="https://…"
              sx={{ mt: 1.5, mb: 1 }}
            />
            <Button
              variant="contained"
              sx={{ mt: 1 }}
              onClick={() =>
                updateHeroMutation.mutate({
                  ...heroForm,
                  headline: heroForm.title
                })
              }
              disabled={updateHeroMutation.isLoading}
            >
              Save hero &amp; intro
            </Button>
          </CardContent>
        </Card>
        <Card className="about-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Vision / Mission / Objective</Typography>
            {['vision', 'mission', 'objective'].map((key) => {
              const block = vmoForm[key] || about[key] || {};
              return (
                <Box key={key} sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase">{key}</Typography>
                  <TextField fullWidth size="small" label="Title" value={block.title || ''} onChange={(e) => setVmoForm((p) => ({ ...p, [key]: { ...(p[key] || {}), title: e.target.value } }))} sx={{ mt: 0.5, mb: 1 }} />
                  <TextField fullWidth size="small" multiline rows={2} label="Text" value={block.text || ''} onChange={(e) => setVmoForm((p) => ({ ...p, [key]: { ...(p[key] || {}), text: e.target.value } }))} />
                  <Box sx={{ mt: 1 }}>
                    <IconSelect label="Icon" value={block.icon || ''} onChange={(v) => setVmoForm((p) => ({ ...p, [key]: { ...(p[key] || {}), icon: v } }))} />
                  </Box>
                </Box>
              );
            })}
            <Button variant="contained" sx={{ mt: 1 }} onClick={() => updateVmoMutation.mutate(vmoForm)} disabled={updateVmoMutation.isLoading}>Save VMO</Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card className="about-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Our Projects</Typography>
            <p className="about-hint">
              Public page shows a <strong>5×2-style grid</strong> of buttons (icon + title). Visitors tap a row to expand details on the same page: full description, stats, gallery.
              Optional <strong>Know more</strong> URL appears next to stats only when you fill the link field. Lower <strong>sort order</strong> numbers appear first.
            </p>
            <ProjectList projects={projects} onUpdate={updateProjectMutation.mutate} onDelete={deleteProjectMutation.mutate} onCreate={createProjectMutation.mutate} />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card className="about-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Activities We Do</Typography>
            <p className="about-hint">Add activity cards (title, optional icon).</p>
            <ActivityList activities={activities} onUpdate={updateActivityMutation.mutate} onDelete={deleteActivityMutation.mutate} onCreate={createActivityMutation.mutate} />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Card className="about-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Our Journey (timeline)</Typography>
            <p className="about-hint">Add milestones: year, title, description.</p>
            <JourneyList journey={journey} onUpdate={updateJourneyMutation.mutate} onDelete={deleteJourneyMutation.mutate} onCreate={createJourneyMutation.mutate} />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={4}>
        <Card className="about-admin-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Section titles</Typography>
            <div className="about-form-grid">
              <TextField fullWidth size="small" label="Projects section title" value={sectionForm.projects} onChange={(e) => setSectionForm((p) => ({ ...p, projects: e.target.value }))} />
              <TextField
                fullWidth
                size="small"
                label="Projects section subtitle"
                placeholder="Optional — appears under the heading on the About page"
                value={sectionForm.projectsSubtitle}
                onChange={(e) => setSectionForm((p) => ({ ...p, projectsSubtitle: e.target.value }))}
                multiline
                minRows={2}
              />
              <TextField fullWidth size="small" label="Activities section title" value={sectionForm.activities} onChange={(e) => setSectionForm((p) => ({ ...p, activities: e.target.value }))} />
              <TextField fullWidth size="small" label="Journey section title" value={sectionForm.journey} onChange={(e) => setSectionForm((p) => ({ ...p, journey: e.target.value }))} />
            </div>
            <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => updateSectionTitlesMutation.mutate(sectionForm)} disabled={updateSectionTitlesMutation.isLoading}>Save section titles</Button>
          </CardContent>
        </Card>
        <Card className="about-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>CTA buttons</Typography>
            <div className="about-form-grid">
              <TextField fullWidth size="small" label="Meet Our Team – text" value={ctaForm.teamText} onChange={(e) => setCtaForm((p) => ({ ...p, teamText: e.target.value }))} />
              <TextField fullWidth size="small" label="Meet Our Team – link" value={ctaForm.teamLink} onChange={(e) => setCtaForm((p) => ({ ...p, teamLink: e.target.value }))} />
              <TextField fullWidth size="small" label="Volunteer – text" value={ctaForm.volunteerText} onChange={(e) => setCtaForm((p) => ({ ...p, volunteerText: e.target.value }))} />
              <TextField fullWidth size="small" label="Volunteer – link" value={ctaForm.volunteerLink} onChange={(e) => setCtaForm((p) => ({ ...p, volunteerLink: e.target.value }))} />
            </div>
            <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => updateCtaMutation.mutate(ctaForm)} disabled={updateCtaMutation.isLoading}>Save CTAs</Button>
          </CardContent>
        </Card>
      </TabPanel>
    </div>
  );
};

function ProjectList({ projects, onUpdate, onDelete, onCreate }) {
  const { showToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newIcon, setNewIcon] = useState('');
  const [newLink, setNewLink] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDetail, setNewDetail] = useState('');
  const [newStat1Label, setNewStat1Label] = useState('');
  const [newStat1Value, setNewStat1Value] = useState('');
  const [newStat2Label, setNewStat2Label] = useState('');
  const [newStat2Value, setNewStat2Value] = useState('');
  const [newStat3Label, setNewStat3Label] = useState('');
  const [newStat3Value, setNewStat3Value] = useState('');
  const [newGallery, setNewGallery] = useState('');
  const [newOrder, setNewOrder] = useState('0');
  const [newIsActive, setNewIsActive] = useState(true);
  const uploadInputRef = useRef(null);
  const editGalleryUploadRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editGalleryUploading, setEditGalleryUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const handleAdd = () => {
    if (!newTitle.trim()) return;
    onCreate({
      title: newTitle.trim(),
      icon: newIcon.trim() || null,
      link: newLink.trim() || null,
      description: newDescription.trim() || null,
      detail: newDetail.trim() || null,
      stat1Label: newStat1Label.trim() || null,
      stat1Value: newStat1Value.trim() || null,
      stat2Label: newStat2Label.trim() || null,
      stat2Value: newStat2Value.trim() || null,
      stat3Label: newStat3Label.trim() || null,
      stat3Value: newStat3Value.trim() || null,
      gallery: newGallery.trim() || null,
      order: newOrder === '' ? 0 : Number(newOrder),
      isActive: newIsActive
    });
    setNewTitle('');
    setNewIcon('');
    setNewLink('');
    setNewDescription('');
    setNewDetail('');
    setNewStat1Label('');
    setNewStat1Value('');
    setNewStat2Label('');
    setNewStat2Value('');
    setNewStat3Label('');
    setNewStat3Value('');
    setNewGallery('');
    setNewOrder('0');
    setNewIsActive(true);
    setAdding(false);
  };
  return (
    <div className="about-list">
      {projects.map((p) => (
        <div key={p.id} className="about-list-item">
          {editingId === p.id ? (
            <div className="about-edit-block">
              <Typography variant="subtitle2" gutterBottom>Edit project</Typography>
              <div className="about-form-grid">
                <TextField
                  fullWidth
                  size="small"
                  label="Title *"
                  value={editDraft.title}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, title: e.target.value }))}
                />
                <IconSelect
                  label="Icon"
                  value={editDraft.icon || ''}
                  onChange={(v) => setEditDraft((prev) => ({ ...prev, icon: v }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Short description (optional)"
                  value={editDraft.description || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, description: e.target.value }))}
                  helperText="Optional; main body uses the expanded description below."
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                  label="Expanded description (shown when visitor opens this project)"
                  value={editDraft.detail || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, detail: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 1 value (e.g. 5100+)"
                  value={editDraft.stat1Value || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat1Value: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 1 label (e.g. Adventure Camps)"
                  value={editDraft.stat1Label || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat1Label: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 2 value"
                  value={editDraft.stat2Value || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat2Value: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 2 label"
                  value={editDraft.stat2Label || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat2Label: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 3 value"
                  value={editDraft.stat3Value || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat3Value: e.target.value }))}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Stat 3 label"
                  value={editDraft.stat3Label || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, stat3Label: e.target.value }))}
                />
                {splitGalleryUrls(editDraft.gallery).length > 0 ? (
                  <div className="about-gallery-preview-grid">
                    {splitGalleryUrls(editDraft.gallery).map((url, idx) => (
                      <div key={`${url}-${idx}`} className="about-gallery-preview-item">
                        <img src={url} alt="" loading="lazy" />
                        <button
                          type="button"
                          className="about-gallery-preview-remove"
                          aria-label="Remove image"
                          onClick={() =>
                            setEditDraft((prev) => ({
                              ...prev,
                              gallery: removeGalleryUrlAt(prev.gallery, idx)
                            }))
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    ref={editGalleryUploadRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setEditGalleryUploading(true);
                      try {
                        const res = await adminAPI.uploadImage(file);
                        const url = res.data?.data?.url;
                        if (url) {
                          setEditDraft((prev) => ({
                            ...prev,
                            gallery: prev.gallery ? `${String(prev.gallery).trim()}\n${url}` : url
                          }));
                        }
                      } catch (err) {
                        showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
                      } finally {
                        setEditGalleryUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Upload size={16} />}
                    onClick={() => editGalleryUploadRef.current?.click()}
                    disabled={editGalleryUploading}
                  >
                    {editGalleryUploading ? 'Uploading…' : 'Append image from device'}
                  </Button>
                </div>
                <TextField
                  fullWidth
                  size="small"
                  label="Know more URL (optional)"
                  value={editDraft.link || ''}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, link: e.target.value }))}
                  helperText="If set, a “Know more” link appears on the public page next to stats (new tab)."
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Sort order"
                  value={editDraft.order !== undefined && editDraft.order !== null && editDraft.order !== '' ? editDraft.order : '0'}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, order: e.target.value }))}
                  helperText="Lower numbers appear first in the grid."
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editDraft.isActive !== false}
                      onChange={(e) => setEditDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
                    />
                  }
                  label="Visible on public About page"
                />
              </div>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const { order: ord, ...rest } = editDraft;
                    onUpdate({
                      id: p.id,
                      data: {
                        ...rest,
                        order: ord === '' || ord == null ? 0 : Number(ord),
                        isActive: editDraft.isActive !== false
                      }
                    });
                    setEditingId(null);
                    setEditDraft(null);
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setEditingId(null);
                    setEditDraft(null);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </div>
          ) : (
            <>
              <div>
                <span className="about-list-item-icon" title={p.icon || 'No icon'}>
                  {(() => {
                    const Icon = getIconComponent(p.icon);
                    return Icon ? <Icon size={18} strokeWidth={2} /> : <Minus size={18} />;
                  })()}
                </span>
                <strong>{p.title}</strong>
                <span className="about-list-item-sub" style={{ marginLeft: 8 }}>
                  Order: {p.order ?? 0}
                  {p.isActive === false ? ' · Hidden' : ''}
                </span>
                {p.link ? (
                  <a href={p.link} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>
                    Know more URL
                  </a>
                ) : null}
                {(p.stat1Value || p.stat2Value || p.stat3Value) && (
                  <div className="about-list-item-sub">
                    {[1, 2, 3].map((i) => {
                      const label = p[`stat${i}Label`];
                      const value = p[`stat${i}Value`];
                      if (!value) return null;
                      return (
                        <span key={i}>
                          <strong>{value}</strong>
                          {label ? ` ${label}` : ''}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="contact-item-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingId(p.id);
                    setEditDraft({
                      title: p.title || '',
                      icon: p.icon || '',
                      description: p.description || '',
                      detail: p.detail || '',
                      stat1Value: p.stat1Value || '',
                      stat1Label: p.stat1Label || '',
                      stat2Value: p.stat2Value || '',
                      stat2Label: p.stat2Label || '',
                      stat3Value: p.stat3Value || '',
                      stat3Label: p.stat3Label || '',
                      gallery: p.gallery || '',
                      link: p.link || '',
                      order: p.order != null ? String(p.order) : '0',
                      isActive: p.isActive !== false
                    });
                  }}
                >
                  <Pencil size={14} /> Edit
                </button>
                <button type="button" className="btn btn-secondary contact-btn-danger" onClick={() => onDelete(p.id)}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      {adding ? (
        <div className="about-add-form about-add-form-with-select">
          <TextField fullWidth size="small" label="Title *" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          <IconSelect label="Icon" value={newIcon} onChange={setNewIcon} />
          <TextField fullWidth size="small" label="Short description (optional)" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
          <TextField fullWidth size="small" multiline rows={4} label="Expanded description (on expand)" value={newDetail} onChange={(e) => setNewDetail(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 1 value (e.g. 5100+)" value={newStat1Value} onChange={(e) => setNewStat1Value(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 1 label (e.g. Adventure Camps)" value={newStat1Label} onChange={(e) => setNewStat1Label(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 2 value" value={newStat2Value} onChange={(e) => setNewStat2Value(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 2 label" value={newStat2Label} onChange={(e) => setNewStat2Label(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 3 value" value={newStat3Value} onChange={(e) => setNewStat3Value(e.target.value)} />
          <TextField fullWidth size="small" label="Stat 3 label" value={newStat3Label} onChange={(e) => setNewStat3Label(e.target.value)} />
          {splitGalleryUrls(newGallery).length > 0 ? (
            <div className="about-gallery-preview-grid">
              {splitGalleryUrls(newGallery).map((url, idx) => (
                <div key={`${url}-${idx}`} className="about-gallery-preview-item">
                  <img src={url} alt="" loading="lazy" />
                  <button
                    type="button"
                    className="about-gallery-preview-remove"
                    aria-label="Remove image"
                    onClick={() => setNewGallery((prev) => removeGalleryUrlAt(prev, idx))}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingImage(true);
                try {
                  const res = await adminAPI.uploadImage(file);
                  const url = res.data?.data?.url;
                  if (url) {
                    setNewGallery((prev) => (prev ? `${prev}\n${url}` : url));
                  }
                } catch (err) {
                  showToast({ message: err.response?.data?.message || 'Image upload failed', severity: 'error' });
                } finally {
                  setUploadingImage(false);
                  e.target.value = '';
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Upload size={16} />}
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploadingImage}
            >
              {uploadingImage ? 'Uploading…' : 'Append gallery image'}
            </Button>
          </div>
          <TextField
            fullWidth
            size="small"
            label="Know more URL (optional)"
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            helperText="Shown beside stats on the site only if filled."
          />
          <TextField fullWidth size="small" type="number" label="Sort order" value={newOrder} onChange={(e) => setNewOrder(e.target.value)} helperText="Lower = earlier in the grid" />
          <FormControlLabel control={<Checkbox checked={newIsActive} onChange={(e) => setNewIsActive(e.target.checked)} />} label="Visible on public About page" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleAdd}>Add</Button>
            <Button variant="outlined" size="small" onClick={() => setAdding(false)}>Cancel</Button>
          </Box>
        </div>
      ) : (
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add project</button>
      )}
    </div>
  );
}

function ActivityList({ activities, onUpdate, onDelete, onCreate }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newIcon, setNewIcon] = useState('');
  const handleAdd = () => {
    if (!newTitle.trim()) return;
    onCreate({ title: newTitle.trim(), icon: newIcon.trim() || null });
    setNewTitle('');
    setNewIcon('');
    setAdding(false);
  };
  return (
    <div className="about-list">
      {activities.map((a) => (
        <div key={a.id} className="about-list-item">
          <div>
            <span className="about-list-item-icon" title={a.icon || 'No icon'}>{(() => { const Icon = getIconComponent(a.icon); return Icon ? <Icon size={18} strokeWidth={2} /> : <Minus size={18} />; })()}</span>
            <strong>{a.title}</strong>
          </div>
          <div className="contact-item-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { const t = prompt('Title', a.title); if (t != null) onUpdate({ id: a.id, data: { ...a, title: t } }); }}><Pencil size={14} /> Edit</button>
            <button type="button" className="btn btn-secondary contact-btn-danger" onClick={() => onDelete(a.id)}><Trash2 size={14} /> Delete</button>
          </div>
        </div>
      ))}
      {adding ? (
        <div className="about-add-form about-add-form-with-select">
          <TextField fullWidth size="small" label="Title *" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          <IconSelect label="Icon" value={newIcon} onChange={setNewIcon} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleAdd}>Add</Button>
            <Button variant="outlined" size="small" onClick={() => setAdding(false)}>Cancel</Button>
          </Box>
        </div>
      ) : (
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add activity</button>
      )}
    </div>
  );
}

function JourneyList({ journey, onUpdate, onDelete, onCreate }) {
  const [adding, setAdding] = useState(false);
  const [year, setYear] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const handleAdd = () => {
    if (!title.trim() || !year.trim()) return;
    onCreate({ year: Number(year), title: title.trim(), description: description.trim() || null });
    setYear('');
    setTitle('');
    setDescription('');
    setAdding(false);
  };
  return (
    <div className="about-list">
      {journey.map((j) => (
        <div key={j.id} className="about-list-item">
          <div><strong>{j.year}</strong> – {j.title}<br /><small>{j.description}</small></div>
          <div className="contact-item-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { const t = prompt('Title', j.title); if (t != null) onUpdate({ id: j.id, data: { ...j, title: t } }); }}><Pencil size={14} /> Edit</button>
            <button type="button" className="btn btn-secondary contact-btn-danger" onClick={() => onDelete(j.id)}><Trash2 size={14} /> Delete</button>
          </div>
        </div>
      ))}
      {adding ? (
        <div className="about-add-form">
          <input className="input" type="number" placeholder="Year *" value={year} onChange={(e) => setYear(e.target.value)} />
          <input className="input" placeholder="Title *" value={title} onChange={(e) => setTitle(e.target.value)} />
          <input className="input" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          <button type="button" className="btn btn-primary" onClick={handleAdd}>Add</button>
          <button type="button" className="btn btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      ) : (
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add milestone</button>
      )}
    </div>
  );
}

export default AboutManagement;
